import { tt } from '@agos/shared'

/**
 * Attaching the paper to a supplier invoice.
 *
 * Two endpoints, both `multipart/form-data` and both `{main_admin, admin}`:
 *
 * - `POST /invoices/{id}/upload-pdf`, field **`pdf`** — the supplier's own document. PDF only:
 *   *"Povolené sú iba PDF súbory"*.
 * - `POST /invoices/{id}/upload-attachment`, field **`photo`** — a photograph of the paper. Images only,
 *   and the extension is derived from the declared content type first, filename second, narrowed to a
 *   known list either way, because *"trusting the uploaded extension is what lets somebody store `.html`
 *   in a directory that is served back over HTTP"*.
 *
 * **Each invoice holds exactly one of each, and uploading replaces.** `storePdf` sets `pdfBlobKey` and
 * `storeAttachment` sets `attachmentBlobKey` — single columns, not a list. So a second photograph
 * overwrites the first and the first is gone from the invoice with nothing said. That is the whole
 * reason this module exists rather than a bare file input: the replacement is stated before it happens.
 *
 * **And there is no delete.** No endpoint removes an attachment — the only way to clear one is to
 * upload something else over it, which is not the same thing. Rather than offer a button that would
 * have to fake it, the screen says what is actually possible. Whether a delete should exist is filed
 * for the owner.
 *
 * Measured: both keys are null on every invoice in this data, so nothing has ever been attached.
 */

export type AttachmentKind = 'pdf' | 'photo'

export const PDF_TYPE = 'application/pdf'
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

/** The form field each endpoint reads. Getting this wrong is a 400 with an unhelpful message. */
export function fieldNameFor(kind: AttachmentKind): string {
  return kind === 'pdf' ? 'pdf' : 'photo'
}

export function endpointFor(kind: AttachmentKind, invoiceId: number): string {
  return kind === 'pdf'
    ? `/api/v1/invoices/${invoiceId}/upload-pdf`
    : `/api/v1/invoices/${invoiceId}/upload-attachment`
}

/** The server's own rules, applied before the upload rather than after it. */
export function fileProblem(kind: AttachmentKind, file: { type: string; name: string }): string | null {
  if (kind === 'pdf') {
    const looksPdf = file.type === PDF_TYPE || file.name.toLowerCase().endsWith('.pdf')
    return looksPdf ? null : tt('Povolené sú iba PDF súbory')
  }
  if (!IMAGE_TYPES.includes(file.type)) {
    // The server narrows by declared type first and filename second; it will refuse anything else.
    return tt('Priložiť sa dá iba obrázok (JPG, PNG, WebP, GIF).')
  }
  return null
}

/**
 * Said before the button when there is already something attached.
 *
 * The column holds one key. Uploading a second photograph does not add it — it replaces the first, and
 * the first is then unreachable from this invoice.
 */
export function replaceWarning(kind: AttachmentKind, existingKey: string | null): string {
  if (existingKey === null || existingKey === '') return ''
  return kind === 'pdf'
    ? tt('K faktúre už je PDF priložené - nahratím sa nahradí, pôvodné sa z faktúry stratí.')
    : tt('K faktúre už je fotka priložená - nahratím sa nahradí, pôvodná sa z faktúry stratí.')
}

export function hasAttachment(key: string | null | undefined): boolean {
  return typeof key === 'string' && key !== ''
}

/** Where an attached file is served from. */
export function attachmentUrl(key: string | null | undefined): string | null {
  if (!hasAttachment(key)) return null
  return `/api/v1/uploads/${encodeURIComponent(key as string)}`
}

/**
 * Said where a delete button would be.
 *
 * No endpoint removes an attachment. Offering a button that overwrites with an empty file, or that
 * clears the column through some other write, would be inventing a capability the API does not have —
 * and on a document trail that is the wrong thing to invent.
 */
export function noDeleteNote(): string {
  return tt(
    'Odobrať prílohu sa zatiaľ nedá - API na to nemá koncový bod. Nahradiť ju novým súborom áno.',
  )
}

/** Said when nothing is attached, so an empty panel is not read as a failed load. */
export function emptyNote(): string {
  return tt('K tejto faktúre nie je priložený žiadny dokument.')
}
